import muaLogo from 'assets/mua/logo.svg'
import muaLogoTransparent from 'assets/mua/logo-transparent.svg'
import muaAuthImage from 'assets/shared/muaInsurance.png'
import type { TenantConfig, TenantSeedTheme } from '../types'
import {
  generateTheme,
  generateDarkTheme,
  generateColors
} from '../generateTheme'

const seed: TenantSeedTheme = {
  primary: '25 90% 50%',
  secondary: '35 85% 55%',
  emailPrimaryHex: '#ff671d',
  emailSecondaryHex: '#0033a1'
}

const muaConfig: TenantConfig = {
  id: 'mua',
  name: 'MUA Insurance',
  tagline: 'Insurance Made Simple',
  logo: muaLogo,
  logoTransparent: muaLogoTransparent,
  authImage: muaAuthImage,
  authBrandBackground: '#fff5ee',
  favicon: '/favicon.png',
  websiteUrl: 'https://mua.co.ke',
  supportEmail: 'support@mua.co.ke',
  supportPhone: '+254 700 000 000',
  twitterHandle: '@MUAInsurance',
  theme: generateTheme(seed),
  darkTheme: generateDarkTheme(seed),
  colors: generateColors('#EF6C00', '#FFA726'),
  content: {
    heroTitle: 'Motor Insurance',
    heroTitleAccent: 'Done Right',
    heroDescription:
      'Get your vehicle covered with MUA — fast quotes, competitive rates and hassle-free claims. Buy your motor policy online and hit the road with confidence.',
    ratingText: '4.6/5 from 25,000+ customers',
    stats: [
      { value: '25K+', label: 'Happy Customers' },
      { value: '40+', label: 'Product Lines' },
      { value: '24/7', label: 'Customer Support' },
      { value: '99%', label: 'Claims Settled' }
    ],
    howItWorksTitle: 'How It Works',
    howItWorksSubtitle:
      'Getting motor insurance with MUA is quick and straightforward.',
    howItWorks: [
      {
        step: '01',
        title: 'Tell Us About Your Vehicle',
        description:
          'Fill in basic details — registration, make, model and year of manufacture.'
      },
      {
        step: '02',
        title: 'Pick Your Cover',
        description:
          'Choose from our range of motor covers — Comprehensive, Third Party or specialised PSV plans.'
      },
      {
        step: '03',
        title: 'Drive With Confidence',
        description:
          'Complete your payment and get your policy certificate emailed to you instantly.'
      }
    ],
    whyChooseTitle: 'Why Choose MUA?',
    whyChooseSubtitle:
      'Motor insurance that fits your needs and your budget. Quick, transparent and always fair.',
    features: [
      {
        title: 'Wide Coverage',
        description:
          'Comprehensive, third-party or PSV — cover for every type of vehicle.',
        badge: '40+',
        icon: 'shield'
      },
      {
        title: 'Quick Turnaround',
        description:
          'Get your motor policy issued in minutes, not days. Fully digital.',
        badge: '24H',
        icon: 'clock'
      },
      {
        title: 'Expert Team',
        description:
          'Experienced motor insurance professionals ready to assist you anytime.',
        badge: '24/7',
        icon: 'users'
      },
      {
        title: 'Great Value',
        description:
          'Competitive premiums with maximum coverage for your peace of mind.',
        badge: '99%',
        icon: 'document'
      }
    ],
    productsTitle: 'Insurance Products',
    productsSubtitle:
      'Motor insurance is our primary focus, with more products coming soon',
    products: [],
    testimonialsTitle: 'What Our Customers Say',
    testimonialsSubtitle:
      'Hear from vehicle owners who trust MUA for their motor insurance.',
    testimonials: [
      {
        name: 'Samuel Otieno',
        role: 'Taxi Operator',
        quote:
          'MUA gives me great value for my taxi fleet. The online portal makes renewals effortless.',
        rating: 5
      },
      {
        name: 'Faith Muthoni',
        role: 'Private Car Owner',
        quote:
          'After my accident, MUA processed my claim within 48 hours. I could not ask for more.',
        rating: 5
      },
      {
        name: 'Kevin Ochieng',
        role: 'Commercial Driver',
        quote:
          'Affordable premiums and excellent customer service. MUA is my go-to insurer.',
        rating: 4
      }
    ],
    ctaTitle: 'Protect Your Vehicle Today',
    ctaText:
      'Join thousands of vehicle owners who trust MUA for fast, reliable motor insurance.',
    ctaButtonText: 'Get a Quote',
    faqTitle: 'Frequently Asked Questions',
    faqSubtitle: 'Got questions about motor insurance? We have answers.',
    faqItems: [
      {
        question: 'What vehicles can MUA insure?',
        answer:
          'We insure private cars, commercial vehicles, PSVs, motorcycles and special-purpose vehicles.'
      },
      {
        question: 'How quickly will I receive my policy?',
        answer:
          'Your policy certificate is generated instantly after payment and sent to your email.'
      },
      {
        question: 'What happens when I need to make a claim?',
        answer:
          'Log in to your account, submit a claim with the required documents and track the progress in real time.'
      },
      {
        question: 'Do you offer discounts for multiple vehicles?',
        answer:
          'Yes. Fleet and multi-vehicle owners enjoy discounted rates. Contact our team for a custom quote.'
      }
    ],
    shareMessage: 'I am now insured with MUA Insurance!',
    copyrightHolder: 'MUA Insurance'
  },
  seo: {
    title: 'MUA Insurance — Insurance Made Simple in Kenya',
    description:
      'MUA Insurance makes cover simple. Compare quotes and buy motor, health and more insurance online in minutes — no paperwork.',
    keywords: [
      'MUA Insurance',
      'insurance Kenya',
      'compare insurance quotes',
      'motor insurance',
      'buy insurance online'
    ],
    locale: 'en_KE',
    areaServed: ['KE']
  },
  storagePrefix: 'mua',
  minVehicleValue: 500000
}

export default muaConfig
